import { useState, useEffect, useCallback } from "react";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";
import { ArrowRight, AlertTriangle, RefreshCw, Search } from "lucide-react";

export default function UpgradeToolPage() {
  const toast = useToast();
  const [packages, setPackages] = useState([]);
  const [newPlan, setNewPlan] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantResults, setTenantResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/packages").then(r => setPackages(r.data.items || [])).catch(() => {});
  }, []);

  // Search tenants
  const searchTenants = useCallback(async (q) => {
    if (!q || q.length < 2) { setTenantResults([]); return; }
    try {
      const r = await api.get(`/tenants?search=${encodeURIComponent(q)}&page_size=20`);
      setTenantResults(r.data.items || []);
    } catch { setTenantResults([]); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchTenants(tenantSearch), 300);
    return () => clearTimeout(timer);
  }, [tenantSearch, searchTenants]);

  const toggleTenant = (tid) => {
    setSelectedIds(prev => prev.includes(tid) ? prev.filter(x => x !== tid) : [...prev, tid]);
    setPreview(null); setResult(null);
  };

  const removeTenant = (tid) => {
    setSelectedIds(prev => prev.filter(x => x !== tid));
    setPreview(null); setResult(null);
  };

  const runPreview = async () => {
    if (!newPlan || selectedIds.length === 0) {
      toast.error("Select a target plan and at least one tenant");
      return;
    }
    setLoading(true); setPreview(null); setResult(null);
    try {
      const r = await api.get(`/upgrade-tool/preview?new_plan=${newPlan}&tenant_ids=${selectedIds.join(",")}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const runBulk = async () => {
    setLoading(true); setConfirming(false);
    try {
      const r = await api.post("/upgrade-tool/bulk", {
        tenant_ids: selectedIds, new_plan: newPlan, reason: reason || undefined,
      });
      setResult(r.data);
      toast.success(`Upgraded ${r.data.updated || 0} tenants to ${newPlan}`);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upgrade failed");
    } finally { setLoading(false); }
  };

  const planLabel = (slug) => packages.find(p => p.slug === slug)?.name || slug;

  return (
    <div className="space-y-6" data-testid="upgrade-tool-page">
      <div>
        <h1 className="text-2xl font-extrabold text-[#0B0F1A]">Bulk Upgrade Tool</h1>
        <p className="text-sm text-[#5B617A] mt-1">Change plans for multiple tenants at once</p>
      </div>

      {/* Config */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">Target Plan</label>
            <select value={newPlan} onChange={e => { setNewPlan(e.target.value); setPreview(null); setResult(null); }}
              className="input-field w-full" data-testid="upgrade-new-plan">
              <option value="">Select target plan...</option>
              {packages.filter(p => p.active).map(p => (
                <option key={p.slug} value={p.slug}>{p.name} ({p.slug})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Q4 promo upgrade"
              className="input-field w-full" data-testid="upgrade-reason" />
          </div>
        </div>

        {/* Tenant Search + Selection */}
        <div>
          <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">
            Select Tenants ({selectedIds.length} selected)
          </label>
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
                placeholder="Search tenants by name..." className="input-field w-full pl-9" data-testid="upgrade-tenant-search" />
            </div>
          </div>

          {/* Selected Chips */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="upgrade-selected-chips">
              {selectedIds.map(tid => {
                const found = tenantResults.find(t => t.tenant_id === tid);
                return (
                  <span key={tid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#7C5CFF]/5 text-[#7C5CFF] text-xs rounded-lg border border-[#7C5CFF]/10">
                    {found?.name || tid.slice(0,8)}
                    <button onClick={() => removeTenant(tid)} className="hover:text-red-500 ml-0.5">&times;</button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Search Results */}
          {tenantSearch.length >= 2 && tenantResults.length > 0 && (
            <div className="border border-[#E2E8F0] rounded-xl max-h-48 overflow-y-auto bg-white" data-testid="upgrade-tenant-results">
              {tenantResults.map(t => (
                <label key={t.tenant_id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer text-sm border-b border-[#F1F5F9] last:border-0">
                  <input type="checkbox" checked={selectedIds.includes(t.tenant_id)} onChange={() => toggleTenant(t.tenant_id)}
                    className="w-4 h-4 rounded border-[#CBD5E1] text-[#7C5CFF] focus:ring-[#7C5CFF]" />
                  <span className="font-medium text-[#0B0F1A]">{t.name}</span>
                  <span className="text-[#5B617A] text-xs ml-auto">{t.plan}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button onClick={runPreview} disabled={loading || !newPlan || selectedIds.length === 0} data-testid="upgrade-preview-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0B0F1A] text-white text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-40 transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Preview Changes
          </button>
        </div>
      </div>

      {/* Preview Table */}
      {preview && (
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="upgrade-preview-table">
          <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
            <h2 className="font-bold text-[#0B0F1A]">Preview — {preview.will_change_count} of {preview.total} will change</h2>
            <button onClick={() => setConfirming(true)}
              disabled={loading || !preview.will_change_count}
              data-testid="upgrade-confirm-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#7C5CFF] text-white text-sm font-semibold hover:bg-[#6A4DE0] disabled:opacity-40 transition-colors">
              Confirm & Run
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left px-4 py-3 text-xs text-[#5B617A] font-bold uppercase">Tenant</th>
                <th className="text-left px-4 py-3 text-xs text-[#5B617A] font-bold uppercase">Current Plan</th>
                <th className="text-center px-4 py-3"><ArrowRight size={14} className="mx-auto text-[#94A3B8]" /></th>
                <th className="text-left px-4 py-3 text-xs text-[#5B617A] font-bold uppercase">New Plan</th>
                <th className="text-center px-4 py-3 text-xs text-[#5B617A] font-bold uppercase">Will Change</th>
              </tr>
            </thead>
            <tbody>
              {(preview.diffs || []).map(d => (
                <tr key={d.tenant_id} className="border-b border-[#F1F5F9] hover:bg-[#FAFBFD]">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0B0F1A]">{d.name}</p>
                    <p className="text-[10px] text-[#94A3B8]">{d.tenant_id}</p>
                  </td>
                  <td className="px-4 py-3"><PlanPill plan={d.current_plan} label={planLabel(d.current_plan)} /></td>
                  <td className="px-4 py-3 text-center"><ArrowRight size={14} className="mx-auto text-[#94A3B8]" /></td>
                  <td className="px-4 py-3"><PlanPill plan={d.new_plan} label={planLabel(d.new_plan)} /></td>
                  <td className="px-4 py-3 text-center">
                    {d.will_change ? <span className="text-emerald-500 font-bold">Yes</span> : <span className="text-[#94A3B8]">No</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.missing_tenant_ids?.length > 0 && (
            <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-700">
              ⚠ Missing tenant IDs: {preview.missing_tenant_ids.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="upgrade-result">
          <h3 className="font-bold text-emerald-700 mb-1">Bulk Upgrade Complete</h3>
          <p className="text-sm text-emerald-600">
            {result.updated} updated · {result.failed} failed · Target: {result.new_plan}
          </p>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirming(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full border border-[#E2E8F0]" data-testid="upgrade-confirm-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><AlertTriangle size={20} className="text-red-500" /></div>
              <h3 className="text-lg font-bold text-[#0B0F1A]">Confirm Bulk Upgrade</h3>
            </div>
            <p className="text-sm text-[#5B617A] mb-5">
              You are about to change plans for <strong className="text-[#0B0F1A]">{preview?.will_change_count || 0} tenants</strong> to <strong className="text-[#7C5CFF]">{planLabel(newPlan)}</strong>. This action cannot be easily undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-[#5B617A] hover:bg-[#F1F5F9]">Cancel</button>
              <button onClick={runBulk} disabled={loading} data-testid="upgrade-run-btn"
                className="px-5 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
                {loading ? "Running..." : "Yes, Upgrade All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanPill({ plan, label }) {
  const colors = {
    basic: "bg-slate-100 text-slate-600", free: "bg-slate-100 text-slate-600",
    standard: "bg-blue-50 text-blue-600",
    pro: "bg-purple-50 text-purple-600",
    premium: "bg-[#E5399B]/10 text-[#E5399B]",
  };
  return (
    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${colors[plan] || "bg-slate-100 text-slate-600"}`}>
      {label || plan}
    </span>
  );
}
